import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import dayjs from "dayjs";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { API_calculate_metric } from "../../../../../constants";
import { getDefaultAxiosConfig } from "../../../../../utilities/requests";
import DateSelector from "../../../plan/daily_top_bar_components/DateSelector";
import PrebuiltReportShell from "../PrebuiltReportShell";
import {
  formatAxisNumber,
  formatQhClock,
  formatQhTick,
  qhHourTicks,
} from "../../charts/chartFormat";
import { lastCompletedBusinessDay } from "./_shared/reportDates";

// Metric values supported by calculate_metric with Intraday output today.
const PRIMARY_METRICS = [
  { value: "actual_sales", label: "Actual Sales", format: "dollars" },
  { value: "forecasted_sales", label: "Forecasted Sales", format: "dollars" },
  {
    value: "forecasted_adjusted_sales",
    label: "Forecasted Adjusted Sales",
    format: "dollars",
  },
];

const SECONDARY_METRICS = [
  { value: "actual_labor_hours", label: "Actual Hours", format: "hours" },
  {
    value: "scheduled_labor_hours",
    label: "Scheduled Hours",
    format: "hours",
  },
  {
    value: "forecasted_labor_hours",
    label: "Forecasted Hours",
    format: "hours",
  },
  { value: "earned_labor_hours", label: "Earned Hours", format: "hours" },
];

function getMetricMeta(value) {
  return (
    PRIMARY_METRICS.find((m) => m.value === value) ||
    SECONDARY_METRICS.find((m) => m.value === value) ||
    null
  );
}

function formatValue(value, format) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  if (format === "dollars") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  }
  if (format === "hours") {
    return `${n.toFixed(2)} hrs`;
  }
  return n.toLocaleString();
}

const PRIMARY_COLOR = "#6d28d9"; // violet-700
const SECONDARY_COLOR = "#0891b2"; // cyan-600

export default function LaborMetricGraphReport({
  report,
  onBack,
  storePicker,
  homeStore,
  storeNames,
}) {
  // Opens on the store's last completed business day: the report is built
  // from actuals, which aren't complete until the business day ends.
  const [selectedDate, setSelectedDate] = useState(() =>
    lastCompletedBusinessDay(storeNames, homeStore)
  );
  const [primaryMetric, setPrimaryMetric] = useState("actual_sales");
  const [secondaryMetric, setSecondaryMetric] = useState("actual_labor_hours");

  const incrementDay = useCallback((current, days) => {
    const next = new Date(current);
    next.setDate(next.getDate() + days);
    next.setHours(0, 0, 0, 0);
    setSelectedDate(next);
  }, []);

  const [intradayRows, setIntradayRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const dateStr = useMemo(
    () => dayjs(selectedDate).format("YYYY-MM-DD"),
    [selectedDate]
  );

  useEffect(() => {
    if (!homeStore) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await axios.get(API_calculate_metric, {
          params: {
            selectedStores: String(homeStore),
            selectedMetrics: `${primaryMetric},${secondaryMetric}`,
            selectedStartDate: dateStr,
            selectedEndDate: dateStr,
            selectedDateRange: "Custom Range",
            store_id: homeStore,
            comparingMetricsFlag: true,
            activeTab: "Intraday",
            selectedRadioOption: "Default",
          },
          ...getDefaultAxiosConfig(),
        });
        if (cancelled) return;
        const rows = res?.data?.redux?.payload?.intraday_data || [];
        // Sort by qh_num so the line draws left-to-right.
        const sorted = [...rows].sort(
          (a, b) => (a.qh_num ?? 0) - (b.qh_num ?? 0)
        );
        setIntradayRows(sorted);
      } catch (e) {
        if (cancelled) return;
        setError(
          "Couldn't load metrics. Try again or pick different inputs."
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [homeStore, dateStr, primaryMetric, secondaryMetric, retryNonce]);

  const handleRetry = useCallback(() => setRetryNonce((n) => n + 1), []);

  // Pivot the intraday rows into a chart-friendly shape:
  // [{ qh_num, [primaryMetric]: number, [secondaryMetric]: number }, ...]
  // We sum across any duplicates per qh (shouldn't happen with one store +
  // one day, but defensive).
  const chartData = useMemo(() => {
    const byQh = new Map();
    for (const row of intradayRows) {
      const qh = row?.qh_num;
      if (qh == null) continue;
      if (!byQh.has(qh)) byQh.set(qh, { qh_num: qh });
      const slot = byQh.get(qh);
      const p = Number(row[primaryMetric]);
      const s = Number(row[secondaryMetric]);
      if (!Number.isNaN(p)) slot[primaryMetric] = (slot[primaryMetric] || 0) + p;
      if (!Number.isNaN(s))
        slot[secondaryMetric] = (slot[secondaryMetric] || 0) + s;
    }
    return Array.from(byQh.values()).sort((a, b) => a.qh_num - b.qh_num);
  }, [intradayRows, primaryMetric, secondaryMetric]);

  const hourTicks = useMemo(() => qhHourTicks(chartData), [chartData]);

  const primaryMeta = getMetricMeta(primaryMetric);
  const secondaryMeta = getMetricMeta(secondaryMetric);

  const metricSelectSx = {
    height: "36px",
    backgroundColor: "white",
    fontSize: "13px",
    borderRadius: "6px",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6d28d9",
    },
  };

  const filters = (
    <div className="flex flex-wrap items-center gap-3">
      <DateSelector date={selectedDate} increment_day={incrementDay} />
      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-500">Primary</label>
        <Select
          value={primaryMetric}
          onChange={(e) => setPrimaryMetric(e.target.value)}
          size="small"
          sx={metricSelectSx}
        >
          {PRIMARY_METRICS.map((m) => (
            <MenuItem key={m.value} value={m.value} dense>
              {m.label}
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-500">Labor</label>
        <Select
          value={secondaryMetric}
          onChange={(e) => setSecondaryMetric(e.target.value)}
          size="small"
          sx={metricSelectSx}
        >
          {SECONDARY_METRICS.map((m) => (
            <MenuItem key={m.value} value={m.value} dense>
              {m.label}
            </MenuItem>
          ))}
        </Select>
      </div>
    </div>
  );

  const hasData = chartData.some(
    (d) => d[primaryMetric] != null || d[secondaryMetric] != null
  );

  return (
    <PrebuiltReportShell
      report={report}
      onBack={onBack}
      filters={filters}
      storePicker={storePicker}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={handleRetry}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : !hasData ? (
        <div className="text-sm text-gray-500 py-12 text-center border border-slate-200 rounded-lg">
          No intraday data on this day for the selected metrics.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg p-4">
          <div className="mb-3">
            <h4 className="text-sm font-semibold text-slate-700">
              {dayjs(selectedDate).format("dddd, MMMM D, YYYY")}
            </h4>
            <p className="text-xs text-slate-500">
              {primaryMeta?.label} vs. {secondaryMeta?.label}, per 15 minutes
            </p>
          </div>
          <div style={{ width: "100%", height: 380 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis
                  dataKey="qh_num"
                  ticks={hourTicks}
                  tickFormatter={formatQhTick}
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  stroke="#cbd5e1"
                />
                <YAxis
                  yAxisId="primary"
                  orientation="left"
                  tickFormatter={(v) => formatAxisNumber(v, "dollars", true)}
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  stroke="#cbd5e1"
                />
                <YAxis
                  yAxisId="secondary"
                  orientation="right"
                  tickFormatter={(v) => formatAxisNumber(v, "hours")}
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  stroke="#cbd5e1"
                />
                <Tooltip
                  labelFormatter={formatQhClock}
                  formatter={(value, name) => {
                    const meta = getMetricMeta(name);
                    return [
                      formatValue(value, meta?.format),
                      meta?.label || name,
                    ];
                  }}
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  formatter={(name) => getMetricMeta(name)?.label || name}
                />
                <Line
                  yAxisId="primary"
                  type="monotone"
                  dataKey={primaryMetric}
                  stroke={PRIMARY_COLOR}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="secondary"
                  type="monotone"
                  dataKey={secondaryMetric}
                  stroke={SECONDARY_COLOR}
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </PrebuiltReportShell>
  );
}
